import FiltersDef from './FiltersDef';
import type { PaginationState, SortingState, VisibilityState } from '@tanstack/react-table';

import { formatISO, subDays } from '@cobliteam/utils/date';

import type { IFiltersDef } from '../../types';

/**
 * Retrieves the initial filters based on the current URL query parameters.
 * @returns An array of filter definitions with their corresponding values.
 */
const filterGetParamFilters = (): IFiltersDef[] => {
  const queryParams = new URLSearchParams(location.search);

  const filters = FiltersDef.map((filter) => {
    const paramValue = queryParams.get(filter.paramKey);
    if (paramValue) return { ...filter, value: filter?.parseToValue?.(paramValue) };
    return filter;
  });

  return filters;
};

/**
 * Sets the filters in the URL query parameters.
 * If the filter value is not provided, it removes the parameter from the URL.
 *
 * @param filters The filters to set in the URL query parameters.
 */
const filterSetParamFilters = (filter: IFiltersDef) => {
  const queryParams = new URLSearchParams(location.search);

  // update query param
  const isFilterValueArray = Array.isArray(filter.value);
  if ((!isFilterValueArray && filter.value !== undefined) || (isFilterValueArray && filter.value.length > 0)) {
    const paramValue = filter?.parseToParam?.(filter.value) || filter.value;
    if (paramValue) queryParams.set(filter.paramKey, paramValue);
  } else queryParams.delete(filter.paramKey);

  queryParams.size > 0 ? history.pushState(null, '', `${location.pathname}?${queryParams.toString()}`) : history.pushState(null, '', location.pathname);
};

/**
 * Retrieves the initial search value from the URL query parameters.
 * If the 'search' parameter is present, it returns its value.
 * Otherwise, it returns an empty string.
 *
 * @returns The initial search value.
 */
const filterGetParamSearch = (): string => {
  const queryParams = new URLSearchParams(location.search);
  const search = queryParams.get('search');
  return search || '';
};

/**
 * Sets the search value in the URL query parameters.
 * If the search value is not provided, it removes the 'search' parameter from the URL.
 *
 * @param search The search value to set in the URL query parameters.
 */
const filterSetParamSearch = (search: string) => {
  const queryParams = new URLSearchParams(location.search);
  if (search) queryParams.set('search', search);
  else queryParams.delete('search');

  queryParams.size > 0 ? history.pushState(null, '', `${location.pathname}?${queryParams.toString()}`) : history.pushState(null, '', location.pathname);
};

/**
 * Retrieves the initial dates for the data table filters.
 * If the start date and end date are provided in the query parameters,
 * it returns those dates as a DateRange object.
 * Otherwise, it returns the dates for the past 7 days as the default range.
 * @returns The initial dates for the data table filters.
 */
const filterGetParamDates = (subtractDays = 7) => {
  const queryParams = new URLSearchParams(location.search);
  const startDate = queryParams.get('startDate');
  const endDate = queryParams.get('endDate');

  if (startDate && endDate) {
    return {
      startDate: new Date(startDate),
      endDate: new Date(endDate),
    };
  }

  return {
    startDate: subDays(new Date(), subtractDays), // 7 days ago
    endDate: new Date(),
  };
};

/**
 * Sets the dates for the data table filters in the URL query parameters.
 * If the dates are not provided, it removes the 'startDate' and 'endDate' parameters from the URL.
 * @param dates The dates for the data table filters.
 */
const filterSetParamDates = (dates: {
  startDate: Date | number;
  endDate: Date | number;
}) => {
  const queryParams = new URLSearchParams(location.search);
  if (dates.startDate && dates.endDate) {
    queryParams.set('startDate', formatISO(dates.startDate));
    queryParams.set('endDate', formatISO(dates.endDate));
  } else {
    queryParams.delete('startDate');
    queryParams.delete('endDate');
  }

  queryParams.size > 0 ? history.pushState(null, '', `${location.pathname}?${queryParams.toString()}`) : history.pushState(null, '', location.pathname);
};

/**
 * Retrieves the initial columns for the data table.
 * If the 'columns' parameter is present, it returns its value.
 * Otherwise, it returns an empty string.
 */
const filterGetParamColumns = () => {
  const queryParams = new URLSearchParams(location.search);
  const columns = queryParams.get('columns');
  return columns || '';
};

/**
 *  Sets the columns for the data table in the URL query parameters.
 * @param columns
 */
const filterSetParamColumns = (columns: VisibilityState) => {
  const queryParams = new URLSearchParams(location.search);

  // columns=odometer,driver
  const params = Object.keys(columns).filter((key) => columns[key]);

  if (params.length) queryParams.set('columns', params.join(','));
  else queryParams.delete('columns');

  queryParams.size > 0 ? history.pushState(null, '', `${location.pathname}?${queryParams.toString()}`) : history.pushState(null, '', location.pathname);
};

/**
 * Sets the pagination parameters in the URL query parameters.
 * If the pagination parameters are not provided, it removes the 'page' and 'size' parameters from the URL.
 * @param pagination The pagination parameters.
 */
const filterSetParamPagination = (pagination: PaginationState) => {
  const queryParams = new URLSearchParams(location.search);
  const { pageIndex, pageSize } = pagination;

  if (pageIndex && pageSize) {
    queryParams.set('page', pageIndex.toString());
    queryParams.set('size', pageSize.toString());
  } else {
    queryParams.delete('page');
    queryParams.delete('size');
  }

  history.replaceState(null, '', `${location.pathname}?${queryParams.toString()}`);
};

/**
 * Retrieves the pagination parameters from the URL query parameters.
 * If the 'page' and 'size' parameters are present, it returns their values.
 * Otherwise, it returns the default values: page = 0 and size = 10.
 * @returns The pagination parameters.
 */
const filterGetParamPagination = () => {
  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get('page');
  const size = queryParams.get('size');

  return {
    pageIndex: page ? Number.parseInt(page, 10) : 0,
    pageSize: size ? Number.parseInt(size, 10) : 10,
  };
};

/**
 * Retrieves the sorting parameters from the URL query parameters.
 * If the 'sort' parameter is present, it returns its value.
 * Otherwise, it returns an empty array.
 * @returns The sorting parameters.
 */
const filterGetParamSort = () => {
  const queryParams = new URLSearchParams(location.search);
  const sort = queryParams.get('sort');

  if (sort) {
    const sortArray = sort.split(',');
    return [
      {
        id: sortArray[0],
        desc: sortArray[1] === 'desc',
      },
    ];
  }

  return [];
};

/**
 * Sets the sorting parameters in the URL query parameters.
 * If the sorting parameters are not provided, it removes the 'sort' parameter from the URL.
 * @param sort The sorting parameters.
 */
const filterSetParamSort = (sort: SortingState) => {
  const queryParams = new URLSearchParams(location.search);
  const sortParam = sort?.map((s) => `${s.id},${s.desc ? 'desc' : 'asc'}`).join(',');

  if (sortParam) queryParams.set('sort', sortParam);
  else queryParams.delete('sort');

  history.pushState(null, '', `${location.pathname}?${queryParams.toString()}`);
};

/**
 * Sets the custom parameters in the URL query parameters.
 * If the value is not provided, it removes the parameter from the URL.
 * @param value The value to set in the URL query parameters.
 * @param key The key of the parameter.
 */
const filterSetCustomParams = <T,>(key: string, value?: T) => {
  const queryParams = new URLSearchParams(location.search);
  if (value) queryParams.set(key, value.toString());
  else queryParams.delete(key);

  queryParams.size > 0 ? history.pushState(null, '', `${location.pathname}?${queryParams.toString()}`) : history.pushState(null, '', location.pathname);
};

/**
 * Retrieves the custom parameters from the URL query parameters.
 * If the parameter is present, it returns its value.
 * Otherwise, it returns an empty string.
 * @param key The key of the parameter.
 * @returns The value of the custom parameter.
 */
const filterGetCustomParams = <T,>(key: string): T | undefined => {
  const queryParams = new URLSearchParams(location.search);
  return queryParams.get(key) as T | undefined;
};

export {
  // dates
  filterGetParamDates,
  filterSetParamDates,
  // filters
  filterGetParamFilters,
  filterSetParamFilters,
  // search
  filterGetParamSearch,
  filterSetParamSearch,
  // columns
  filterGetParamColumns,
  filterSetParamColumns,
  // pagination
  filterGetParamPagination,
  filterSetParamPagination,
  // sort
  filterGetParamSort,
  filterSetParamSort,
  // custom
  filterGetCustomParams,
  filterSetCustomParams,
};
